"use client";

import React, { useEffect, useRef, useState } from "react";
import styles from "./MMTab.module.scss";
import { mmTabData, mmTabDataForApp } from "./mmTab.data";
import Button from "@components/atoms/Button";
import Image from "@components/atoms/Image";
import { trackEventWithCommonProps, EventType } from "@utils/eventTracker";
import assetLoader from "@utils/internalImageLoder";

const STICKY_HEADER_HEIGHT = 135;
const ACTIVE_SECTION_TOLERANCE = 24;

interface Tab {
  id: string;
  label: string;
}

interface MMTabProps {
  showExpertTab?: boolean;
  brandName?: string;
  modelName?: string;
  cityName?: string;
  showComparisonTab?: any;
  carAvailability?: string;
  pageType?: string;
  isSellable?: boolean;
  isInApp?: boolean;
}

const MMTab = ({
  showExpertTab = true,
  brandName,
  modelName,
  cityName,
  showComparisonTab = true,
  carAvailability = "Yes",
  pageType = "MM page",
  isSellable,
  isInApp
}: MMTabProps) => {
  let filteredTabs = isInApp ? mmTabDataForApp : mmTabData;

  if (!showExpertTab) {
    filteredTabs = filteredTabs.filter((tab) => tab.id !== "expert-review");
  }

  if (!showComparisonTab) {
    filteredTabs = filteredTabs.filter((tab) => tab.id !== "comparison");
  }

  if (!isSellable) {
    filteredTabs = filteredTabs.filter((tab) => tab.id !== "chat");
  }
  const getInitialTab = () => {
    const expertTab = filteredTabs.find((tab) => tab.id === "expert-review");
    return expertTab ? expertTab.id : null;
  };

  const [activeTab, setActiveTab] = useState<string | null>(getInitialTab());
  const isClickScroll = useRef(false);
  const clickScrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const shouldCenterAlign = filteredTabs.length <= 3;
  const filteredTabIds = filteredTabs.map((tab) => tab.id).join(",");

  const scrollActiveTabIntoView = (tabId: string | null) => {
    if (!tabId) return;

    const activeTabButton = document.querySelector(
      `[data-tab-id="${tabId}"]`
    ) as HTMLElement | null;

    activeTabButton?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  };

  const getCurrentSection = () => {
    let closestSection: string | null = null;
    let closestDistance = Number.POSITIVE_INFINITY;

    for (const tab of filteredTabs) {
      const section = document.getElementById(tab.id);

      if (!section) continue;

      const rect = section.getBoundingClientRect();
      const sectionIsVisible =
        rect.bottom > STICKY_HEADER_HEIGHT &&
        rect.top < window.innerHeight;

      if (!sectionIsVisible) continue;

      const distanceFromStickyHeader = Math.abs(
        rect.top - STICKY_HEADER_HEIGHT
      );
      const adjustedDistance =
        rect.top >= STICKY_HEADER_HEIGHT &&
        rect.top <= STICKY_HEADER_HEIGHT + ACTIVE_SECTION_TOLERANCE
          ? 0
          : distanceFromStickyHeader;

      if (adjustedDistance < closestDistance) {
        closestDistance = adjustedDistance;
        closestSection = tab.id;
      }
    }

    return closestSection;
  };

  const handleTabClick = (tab: Tab) => {
    setActiveTab(tab.id);
    isClickScroll.current = true;

    if (brandName && modelName) {
      trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
        pageType: pageType,
        additionalProps: {
          action_type: "tap_btn",
          action_name: "tap_btn_mm_header",
          selected_value: tab.label,
          vehicle_make: brandName,
          vehicle_model: modelName,
          city_name: cityName,
          car_availability: carAvailability,
        },
      });
    }
    const clickedTabButton = document.querySelector(
      `[data-tab-id="${tab.id}"]`
    ) as HTMLElement;
    if (clickedTabButton) {
      scrollActiveTabIntoView(tab.id);
    }

    const element = document.getElementById(tab.id);
    if (element) {
      const yOffset = -STICKY_HEADER_HEIGHT;
      const y =
        element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }

    if (clickScrollTimeoutRef.current) {
      clearTimeout(clickScrollTimeoutRef.current);
    }

    clickScrollTimeoutRef.current = setTimeout(() => {
      isClickScroll.current = false;
    }, 1200);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (isClickScroll.current) return;

      const currentSection = getCurrentSection();
      setActiveTab((prevTab) =>
        prevTab === currentSection ? prevTab : currentSection
      );
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (clickScrollTimeoutRef.current) {
        clearTimeout(clickScrollTimeoutRef.current);
      }
    };
  }, [filteredTabIds]);

  useEffect(() => {
    scrollActiveTabIntoView(activeTab);
  }, [activeTab]);

  return (
    <div
      className={`${styles.tabNav} ${
        shouldCenterAlign ? styles.centerAligned : ""
      } ${isInApp ? styles.tabNavForApp : ""}`}
      role="navigation"
    >
      {filteredTabs.map((tab) => (
        <Button
          size="ds"
          key={tab.id}
          className={`${styles.tabButton} ${
            activeTab === tab.id ? styles.active : ""
          } ${tab.id === "chat" ? styles.chatTab : ""}`}
          onClick={() => handleTabClick(tab)}
          role="tab"
          aria-selected={activeTab === tab.id}
          data-tab-id={tab.id}
        >
          <div className={`${styles.tabWrapper} ${styles.chatTabWrapper}`}>
            {tab.id === "chat" && (
              <Image
                src={`${assetLoader()}/icons/tab/chatIcon.svg`}
                alt="Chat Icon"
                width={18}
                height={18}
                className={styles.chatIcon}
              />
            )}
            {tab.label}
          </div>
        </Button>
      ))}
    </div>
  );
};

export default MMTab;
